import { SEARCH_PARAM } from '@/constants/searchParams';
import { SEARCH_TYPE } from '@/constants/searchType';
import { PUBLIC_SEARCH_STATE_PARAM } from '@/lib/saleCataloguePaths';

export type SearchType =
  | typeof SEARCH_TYPE.ANIMAL
  | typeof SEARCH_TYPE.MEMBERS
  | typeof SEARCH_TYPE.MATING
  | typeof SEARCH_TYPE.SALE_CATALOGUES
  | typeof SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS
  | typeof SEARCH_TYPE.SEMEN_CATALOGUES_DETAIL_ANIMALS
  | typeof SEARCH_TYPE.SALE_CATALOGUES_NOTES
  | typeof SEARCH_TYPE.DOWNLOADS
  | typeof SEARCH_TYPE.ONLINE_REGISTRATIONS;

const EBV_PERCENTAGES_PATH = 'ebv-percentages';
const ANIMALS_SUBPATHS = new Set([SEARCH_TYPE.MATING, EBV_PERCENTAGES_PATH]);

/**
 * Society-scoped public database UI lives under /{org}/animals, /members, /sales (list + /sales/{id}), /external (no /search).
 */
const SOCIETY_PUBLIC_PREFIX =
  /^\/[^/]+\/(animals|members|sales|external)(\/|$)/;

/**
 * Derive internal SearchType from society public pathname.
 * Returns null if pathname is not under a known society public route.
 */
export function parsePathnameToSearchType(
  pathname: string | null,
): SearchType | null {
  if (!pathname || !SOCIETY_PUBLIC_PREFIX.test(pathname)) return null;

  const tailMatch = pathname.match(/^\/[^/]+\/(.+)$/);
  const segments = (tailMatch?.[1] ?? '').split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const [a, b, c] = segments;

  if (a === 'animals') {
    if (segments.length === 1) return SEARCH_TYPE.ANIMAL;
    if (segments.length === 2 && b === 'sales')
      return SEARCH_TYPE.SALE_CATALOGUES;
    if (segments.length === 2 && b === SEARCH_TYPE.MATING)
      return SEARCH_TYPE.MATING;
    if (segments.length === 2 && b === EBV_PERCENTAGES_PATH)
      return SEARCH_TYPE.ANIMAL;
    if (segments.length === 2 && b && !ANIMALS_SUBPATHS.has(b))
      return SEARCH_TYPE.ANIMAL;
    return null;
  }

  if (a === 'members') return SEARCH_TYPE.MEMBERS;

  if (a === 'sales') {
    if (segments.length === 1) return SEARCH_TYPE.SALE_CATALOGUES;
    if (segments.length === 2 && b)
      return SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS;
    if (segments.length === 3 && b && c === 'notes')
      return SEARCH_TYPE.SALE_CATALOGUES_NOTES;
    return null;
  }

  if (a === 'external') {
    if (b === 'registrations') return SEARCH_TYPE.ONLINE_REGISTRATIONS;
    if (b === SEARCH_TYPE.DOWNLOADS) return SEARCH_TYPE.DOWNLOADS;
  }

  return null;
}

/**
 * Build pathname + query for a society public URL. Preserves unrelated query keys (e.g. shared, filters).
 * Removes searchType and saleId from query when encoded in the path.
 */
export function pathForSearchType(
  orgCode: string,
  searchType: SearchType,
  existingParams?: URLSearchParams | null,
): { pathname: string; query: URLSearchParams } {
  const q = new URLSearchParams(existingParams?.toString() ?? '');
  q.delete(SEARCH_PARAM.SEARCH_TYPE);

  const base = `/${orgCode}`;

  switch (searchType) {
    case SEARCH_TYPE.ANIMAL:
      q.delete(SEARCH_PARAM.SALE_ID);
      return { pathname: `${base}/animals`, query: q };
    case SEARCH_TYPE.MEMBERS:
      q.delete(SEARCH_PARAM.SALE_ID);
      return { pathname: `${base}/members`, query: q };
    case SEARCH_TYPE.SALE_CATALOGUES:
      q.delete(SEARCH_PARAM.SALE_ID);
      return { pathname: `${base}/sales`, query: q };
    case SEARCH_TYPE.MATING:
      q.delete(SEARCH_PARAM.SALE_ID);
      return { pathname: `${base}/animals/mating`, query: q };
    case SEARCH_TYPE.DOWNLOADS:
      q.delete(SEARCH_PARAM.SALE_ID);
      return { pathname: `${base}/external/downloads`, query: q };
    case SEARCH_TYPE.ONLINE_REGISTRATIONS:
      q.delete(SEARCH_PARAM.SALE_ID);
      return { pathname: `${base}/external/registrations`, query: q };
    case SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS:
    case SEARCH_TYPE.SEMEN_CATALOGUES_DETAIL_ANIMALS: {
      const saleId =
        existingParams?.get(SEARCH_PARAM.SALE_ID) ??
        q.get(SEARCH_PARAM.SALE_ID) ??
        undefined;
      q.delete(SEARCH_PARAM.SALE_ID);
      // Lot listing search is independent of the sale catalogues list search.
      q.delete(PUBLIC_SEARCH_STATE_PARAM);
      q.delete(SEARCH_PARAM.LOT_NO);
      if (!saleId) {
        return { pathname: `${base}/sales`, query: q };
      }
      return { pathname: `${base}/sales/${saleId}`, query: q };
    }
    case SEARCH_TYPE.SALE_CATALOGUES_NOTES: {
      const saleId =
        existingParams?.get(SEARCH_PARAM.SALE_ID) ??
        q.get(SEARCH_PARAM.SALE_ID) ??
        undefined;
      q.delete(SEARCH_PARAM.SALE_ID);
      if (!saleId) {
        return { pathname: `${base}/sales`, query: q };
      }
      return { pathname: `${base}/sales/${saleId}/notes`, query: q };
    }
    default:
      q.delete(SEARCH_PARAM.SALE_ID);
      return { pathname: `${base}/animals`, query: q };
  }
}

export function buildSocietySearchHref(
  orgCode: string,
  searchType: SearchType,
  existingParams?: URLSearchParams | null,
): string {
  const { pathname, query } = pathForSearchType(
    orgCode,
    searchType,
    existingParams,
  );
  const qs = query.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}
